// Fixed-height binning for the comparison heatmap strip.
//
// The strip compresses the whole transcript into one viewport height. On a
// long, densely coded transcript that put hundreds of 1–2px cells on top of
// each other, and whichever segment came later in DOM order painted on top —
// paragraph order, not importance, decided what you saw. Binning fixes that:
// the strip is cut into fixed-height bins, each bin sums how much coded text
// of each agreement state falls into it, and ONE state is chosen per bin.
//
// This is the only rendering path. On a regular transcript a coded segment
// spans many bins, which all resolve to its state and merge back into one
// flush band, so the strip looks as it did with per-segment cells (edges can
// move by less than a bin). Nothing switches on transcript size.
//
// Choosing a bin's state: score(state) = share × PRIORITY[state], highest
// wins, ties to the higher priority. Share is the state's fraction of the
// bin's coded coverage; priority ranks disagreement over partial over
// agreement over one-sided gray, so a red sliver in a mostly-green bin still
// shows while gray only shows where it is essentially alone.
//
// Why no neighbour rule: with 2px bins a stretch of one state occupies its
// own bins and cannot be overpainted by its surroundings, so "a strip of
// green in a sea of red" comes through by construction. A neighbour-novelty
// term was tried and rejected: in any uniformly mixed region the minority
// state is always "novel" relative to its neighbours, which flipped whole
// regions rather than preserving small features. Below the bin size the
// strip genuinely cannot resolve individual excerpts (two per bin on the
// 800-excerpt stress transcript); there the majority shows and the tooltip
// carries the mix.
export const AGREEMENT_STATES = ['conflict', 'partial', 'agree', 'oneSided'];

// Red beats green from ~43% of a bin; gray needs >70% against green.
export const PRIORITY = {
  conflict: 1.0,
  partial: 0.85,
  agree: 0.75,
  oneSided: 0.3,
};

// 2px bins: on a short transcript neighbouring snippets can sit 3–4px apart
// in the strip, and a 4px bin swallowed that gap (two stretches became one
// band). At 2px a gap of two or more strip pixels keeps its uncoded bin.
export const BIN_PX = 2;
// A segment thinner than this still contributes this much to its bin, so a
// single tiny conflict cannot vanish into rounding (the old cells had the
// same 2px floor).
export const MIN_SEGMENT_PX = 2;

const emptyCoverage = () => ({ conflict: 0, partial: 0, agree: 0, oneSided: 0 });

const totalOf = (coverage) =>
  AGREEMENT_STATES.reduce((sum, state) => sum + coverage[state], 0);

// Distribute each segment's pixel extent over the bins it overlaps.
const accumulateCoverage = (segments, binCount, binPx) => {
  const bins = Array.from({ length: binCount }, emptyCoverage);
  segments.forEach(({ top, height, classification }) => {
    if (!PRIORITY[classification]) return;
    const h = Math.max(MIN_SEGMENT_PX, height || 0);
    const start = Math.max(0, top);
    const end = Math.min(binCount * binPx, start + h);
    if (end <= start) return;
    const first = Math.floor(start / binPx);
    const last = Math.min(binCount - 1, Math.floor((end - 1e-9) / binPx));
    for (let i = first; i <= last; i += 1) {
      const binTop = i * binPx;
      const overlap = Math.min(end, binTop + binPx) - Math.max(start, binTop);
      if (overlap > 0) bins[i][classification] += overlap;
    }
  });
  return bins;
};

export const chooseState = (coverage) => {
  const total = totalOf(coverage);
  if (total <= 0) return null;
  let best = null;
  let bestScore = -1;
  AGREEMENT_STATES.forEach((state) => {
    if (coverage[state] <= 0) return;
    const score = (coverage[state] / total) * PRIORITY[state];
    if (score > bestScore) {
      bestScore = score;
      best = state;
    }
  });
  return best;
};

const LABELS = {
  conflict: 'Disagreement',
  partial: 'Partial agreement',
  agree: 'Coders agree',
  oneSided: 'Coded by one coder',
};

// Tooltip: the chosen state's label, plus the mix when the run isn't pure,
// so the strip's meaning never rides on hue alone.
export const describeRun = (classification, coverage) => {
  const total = totalOf(coverage);
  const parts = AGREEMENT_STATES
    .filter((state) => coverage[state] > 0)
    .map((state) => `${Math.round((coverage[state] / total) * 100)}% ${LABELS[state].toLowerCase()}`);
  return parts.length > 1 ? `${LABELS[classification]} · ${parts.join(', ')}` : LABELS[classification];
};

/**
 * segments: [{ top, height, classification }] in strip pixels.
 * Returns runs of adjacent same-state bins merged into one cell each:
 * [{ key, classification, top, height, coverage, label }].
 */
export const buildHeatmapCells = (segments, stripHeight, options = {}) => {
  const binPx = options.binPx || BIN_PX;
  const binCount = Math.max(1, Math.ceil(stripHeight / binPx));
  const bins = accumulateCoverage(segments, binCount, binPx);

  // Per-bin states, then absorb ISOLATED single bins: a bin whose two
  // neighbours are both coded, share one state, and that state outranks or
  // equals the bin's own. On a dense transcript this removes the one-bin
  // flicker of agreement/partial/gray inside a stretch of disagreement (and
  // of partial/gray inside agreement) without ever hiding a lone red — red
  // outranks everything, so nothing absorbs it. On a regular transcript a
  // coded bin flanked by two coded bins of another state only occurs where
  // three snippets touch, and the middle one would have to be lower
  // priority than both neighbours — see heatmapBins.test.js.
  const states = bins.map(chooseState);
  const absorbed = states.slice();
  for (let i = 1; i < states.length - 1; i += 1) {
    const s = states[i];
    const l = states[i - 1];
    const r = states[i + 1];
    if (s && l && r && l === r && l !== s && PRIORITY[l] >= PRIORITY[s]) absorbed[i] = l;
  }

  const cells = [];
  let run = null;
  bins.forEach((coverage, i) => {
    const state = absorbed[i];
    if (state && run && run.classification === state) {
      run.height += binPx;
      AGREEMENT_STATES.forEach((s) => { run.coverage[s] += coverage[s]; });
      return;
    }
    if (run) cells.push(run);
    run = state
      ? { key: `bin-${i}`, classification: state, top: i * binPx, height: binPx, coverage: { ...coverage } }
      : null;
  });
  if (run) cells.push(run);

  return cells.map((cell) => ({
    ...cell,
    // Never let a run overshoot the strip (the last bin may be partial).
    height: Math.min(cell.height, stripHeight - cell.top),
    label: describeRun(cell.classification, cell.coverage),
  }));
};
